import { GlButton, GlFormCheckbox, GlTooltipDirective } from '../../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { parseBoolean } from '@gitlab/frontend-utils';
import { helpPagePath } from '~/helpers/help_page_helper';
import { s__, __ } from '~/locale';
import { detectAndConfirmSensitiveTokens } from '../../../lib/utils/secret_detection.js?vue3';
import { i18n, STATE_CLOSED, STATE_OPEN, LINKED_CATEGORIES_MAP } from '~/work_items/constants';
import { getDraft, clearDraft, updateDraft } from '~/lib/utils/autosave';
import gfmEventHub from '~/vue_shared/components/markdown/eventhub';
import { confirmAction } from '../../../lib/utils/confirm_via_gl_modal/confirm_via_gl_modal.js?vue3';
import { trackSavedUsingEditor } from '../../../vue_shared/components/markdown/tracking.js?vue3';
import MarkdownEditor from '../../../vue_shared/components/markdown/markdown_editor.vue?vue3';
import HelpIcon from '../../../vue_shared/components/help_icon/help_icon.vue?vue3';
import WorkItemStateToggle from '../work_item_state_toggle.vue?vue3';
import WorkItemCloseConfirmModal from '../work_item_close_confirm_modal.vue?vue3';
import CommentFieldLayout from '../../../notes/components/comment_field_layout.vue?vue3';
import workItemByIidQuery from '../../graphql/work_item_by_iid.query.graphql';
import workItemEmailParticipantsByIidQuery from '../../graphql/notes/work_item_email_participants_by_iid.query.graphql';
import { findEmailParticipantsWidget, findLinkedItemsWidget, findHierarchyWidget } from '../../utils.js?vue3';
import workItemLinkedItemsQuery from '../../graphql/work_item_linked_items.query.graphql';
import workItemOpenChildCountQuery from '../../graphql/open_child_count.query.graphql';
export default {
    name: 'WorkItemCommentForm',
    i18n: {
        internal: s__('Notes|Make this an internal note'),
        internalVisibility: s__('Notes|Internal notes are only visible to members with the role of Planner or higher'),
        addInternalNote: __('Add internal note'),
        cancelButtonText: __('Cancel')
    },
    constantOptions: {
        markdownDocsPath: helpPagePath('user/markdown')
    },
    components: {
        CommentFieldLayout,
        GlButton,
        MarkdownEditor,
        GlFormCheckbox,
        HelpIcon,
        WorkItemCloseConfirmModal,
        WorkItemStateToggle
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    props: {
        workItemId: {
            type: String,
            required: true
        },
        workItemType: {
            type: String,
            required: true
        },
        fullPath: {
            type: String,
            required: true
        },
        ariaLabel: {
            type: String,
            required: true
        },
        autosaveKey: {
            type: String,
            required: true
        },
        autosaveKeyInternalNote: {
            type: String,
            required: true
        },
        isSubmitting: {
            type: Boolean,
            required: false,
            default: false
        },
        initialValue: {
            type: String,
            required: false,
            default: ''
        },
        commentButtonText: {
            type: String,
            required: false,
            default: __('Comment')
        },
        markdownPreviewPath: {
            type: String,
            required: true
        },
        newCommentTemplatePaths: {
            type: Array,
            required: false,
            default: ()=>[]
        },
        autocompleteDataSources: {
            type: Object,
            required: false,
            default: ()=>({})
        },
        isNewDiscussion: {
            type: Boolean,
            required: false,
            default: false
        },
        workItemState: {
            type: String,
            required: false,
            default: STATE_OPEN
        },
        autofocus: {
            type: Boolean,
            required: false,
            default: false
        },
        isDiscussionLocked: {
            type: Boolean,
            required: false,
            default: false
        },
        isWorkItemConfidential: {
            type: Boolean,
            required: false,
            default: false
        },
        workItemIid: {
            type: String,
            required: false,
            default: null
        },
        isDiscussionInternal: {
            type: Boolean,
            required: false,
            default: false
        },
        isDiscussionResolved: {
            type: Boolean,
            required: false,
            default: false
        },
        isDiscussionResolvable: {
            type: Boolean,
            required: false,
            default: false
        },
        hasReplies: {
            type: Boolean,
            required: false,
            default: false
        },
        isReplyForm: {
            type: Boolean,
            required: false,
            default: false
        },
        hasEmailParticipantsWidget: {
            type: Boolean,
            required: false,
            default: false
        },
        parentId: {
            type: String,
            required: false,
            default: null
        },
        hideFullscreenMarkdownButton: {
            type: Boolean,
            required: false,
            default: false
        },
        uploadsPath: {
            type: String,
            required: true
        }
    },
    emits: [
        'blur',
        'cancel-editing',
        'error',
        'focus',
        'start-editing',
        'stop-editing',
        'submit-form',
        'toggle-resolve-discussion'
    ],
    data () {
        return {
            commentText: getDraft(this.autosaveKey) || this.initialValue || '',
            updateInProgress: false,
            isNoteInternal: parseBoolean(getDraft(this.autosaveKeyInternalNote)) || false,
            toggleResolveChecked: this.isDiscussionResolved,
            emailParticipants: [],
            workItem: {},
            isCancellingEdit: false,
            blockerItems: [],
            openChildItemsCount: 0,
            closeConfirmModalVisible: false
        };
    },
    computed: {
        formFieldProps () {
            return {
                'aria-label': this.ariaLabel,
                placeholder: __('Write a comment or drag your files here…'),
                id: 'work-item-add-or-edit-comment',
                name: 'work-item-add-or-edit-comment'
            };
        },
        commentButtonTextComputed () {
            return this.isNoteInternal ? this.$options.i18n.addInternalNote : this.commentButtonText;
        },
        getWorkItemData () {
            return {
                confidential: this.isWorkItemConfidential,
                discussion_locked: this.isDiscussionLocked,
                issue_email_participants: this.emailParticipants
            };
        },
        workItemTypeId () {
            return this.workItem?.workItemType?.id;
        },
        showResolveDiscussionToggle () {
            // The edit form only offers the toggle on the first note of a thread with replies.
            return !this.isNewDiscussion && this.isDiscussionResolvable && (this.isReplyForm || this.hasReplies);
        },
        resolveCheckboxLabel () {
            return this.isDiscussionResolved ? __('Reopen thread') : __('Resolve thread');
        },
        canMarkNoteAsInternal () {
            return this.workItem?.userPermissions?.markNoteAsInternal;
        },
        canUpdate () {
            return this.workItem?.userPermissions?.updateWorkItem;
        },
        showInternalNoteCheckbox () {
            return this.canMarkNoteAsInternal && this.isNewDiscussion;
        },
        restrictedToolBarItems () {
            if (this.hideFullscreenMarkdownButton) {
                return [
                    'full-screen'
                ];
            }
            return [];
        },
        showStateButton () {
            return this.isNewDiscussion && this.canUpdate && !(this.workItemState === STATE_CLOSED && this.isDiscussionLocked);
        },
        hasCloseQuickAction () {
            return /^\s*\/close\s*$/im.test(this.commentText) && this.workItemState === STATE_OPEN;
        },
        isBlockedByOpenItems () {
            return this.hasCloseQuickAction && this.blockerItems.length > 0;
        },
        hasOpenChildItems () {
            return this.hasCloseQuickAction && this.openChildItemsCount > 0;
        }
    },
    apollo: {
        emailParticipants: {
            query: workItemEmailParticipantsByIidQuery,
            variables () {
                return {
                    fullPath: this.fullPath,
                    iid: this.workItemIid,
                    useWorkItemFeatures: true
                };
            },
            skip () {
                // Don't request email participants if the widget is not available
                return !this.workItemIid || !this.hasEmailParticipantsWidget;
            },
            error () {
                this.$emit('error', i18n.fetchError);
            },
            update (data) {
                return findEmailParticipantsWidget(data?.namespace?.workItem)?.emailParticipants?.nodes || [];
            }
        },
        workItem: {
            query: workItemByIidQuery,
            variables () {
                return {
                    fullPath: this.fullPath,
                    iid: this.workItemIid,
                    useWorkItemFeatures: true
                };
            },
            update (data) {
                return data.namespace?.workItem ?? {};
            },
            skip () {
                return !this.workItemIid;
            },
            error () {
                this.$emit('error', i18n.fetchError);
            }
        },
        blockerItems: {
            query: workItemLinkedItemsQuery,
            variables () {
                return {
                    fullPath: this.fullPath,
                    iid: this.workItemIid,
                    useWorkItemFeatures: true
                };
            },
            skip () {
                return !this.workItemIid || !this.hasCloseQuickAction;
            },
            update ({ namespace }) {
                if (!namespace?.workItem) return [];
                const linkedWorkItems = findLinkedItemsWidget(namespace.workItem)?.linkedItems?.nodes || [];
                return linkedWorkItems.filter((item)=>item.linkType === LINKED_CATEGORIES_MAP.IS_BLOCKED_BY && item.workItemState !== STATE_CLOSED);
            },
            error (e) {
                this.$emit('error', e.message || i18n.fetchError);
            }
        },
        openChildItemsCount: {
            query: workItemOpenChildCountQuery,
            variables () {
                return {
                    fullPath: this.fullPath,
                    iid: this.workItemIid,
                    useWorkItemFeatures: true
                };
            },
            skip () {
                return !this.workItemIid || !this.hasCloseQuickAction;
            },
            update ({ namespace }) {
                if (!namespace?.workItem) return 0;
                const countsByType = findHierarchyWidget(namespace.workItem)?.rolledUpCountsByType;
                if (!countsByType) return 0;
                return countsByType.reduce((acc, curr)=>acc + curr.countsByState.opened, 0);
            }
        }
    },
    methods: {
        handleKeydownUpArrow (e) {
            if (this.commentText === '') {
                gfmEventHub.$emit('edit-current-user-last-note', e);
            }
        },
        setCommentText (newText) {
            /**
       * https://gitlab.com/gitlab-org/gitlab/-/issues/388314
       *
       * While the form is saving using meta+enter,
       * avoid updating the data which is cleared after form submission.
       */ if (!this.isSubmitting) {
                this.commentText = newText;
                updateDraft(this.autosaveKey, this.commentText);
                if (this.commentText) {
                    this.$emit('start-editing');
                } else {
                    this.$emit('stop-editing');
                }
            }
        },
        setInternalNoteCheckbox (value) {
            this.isNoteInternal = value;
            updateDraft(this.autosaveKeyInternalNote, this.isNoteInternal);
        },
        async cancelEditing () {
            if (this.isCancellingEdit) return;
            // Don't cancel if autosuggest open in plain text editor
            if (this.$refs.markdownEditor.$el.querySelector('textarea')?.classList.contains('at-who-active')) {
                return;
            }
            if (this.commentText && this.commentText !== this.initialValue) {
                this.isCancellingEdit = true;
                try {
                    const msg = s__('WorkItem|Are you sure you want to cancel editing?');
                    const confirmed = await confirmAction(msg, {
                        primaryBtnText: __('Discard changes'),
                        cancelBtnText: __('Continue editing'),
                        primaryBtnVariant: 'danger'
                    });
                    if (!confirmed) {
                        return;
                    }
                } finally{
                    this.isCancellingEdit = false;
                }
            }
            this.$emit('cancel-editing');
            clearDraft(this.autosaveKey);
        },
        async submitForm () {
            const isUploadingFile = this.$el.querySelector('.uploading-progress-container:not(.hide)');
            if (this.isSubmitting || isUploadingFile) {
                return;
            }
            const confirmSubmit = await detectAndConfirmSensitiveTokens({
                content: this.commentText
            });
            if (!confirmSubmit) {
                return;
            }
            if (this.isBlockedByOpenItems || this.hasOpenChildItems) {
                this.closeConfirmModalVisible = true;
                return;
            }
            this.proceedWithSubmit();
        },
        proceedWithSubmit () {
            if (this.toggleResolveChecked) {
                this.$emit('toggle-resolve-discussion');
            }
            if (this.$refs.markdownEditor) {
                trackSavedUsingEditor(this.$refs.markdownEditor.isContentEditorActive, 'WorkItem_Comment');
            }
            this.$emit('submit-form', {
                commentText: this.commentText,
                isNoteInternal: this.isNoteInternal
            });
        }
    }
};
